import { AnyAction } from "redux";
import { queueMessage } from "~shared/snackbarsProvider/actions";
import { PENDIENTE } from "~constants/endosos";
import {
  GetEndososRequest,
  GetEndososResponse,
  GetEndososResponseItem,
} from "~api/types";
import { EstadoValue, RowsValue, Services } from "./types";

const baseEndososPoliza = "ListadoEndosos";

export interface ListadoEndososState {
  page: number;
  take: RowsValue;
  searchCriteria: string;
  estado: EstadoValue;
  endosos: GetEndososResponseItem[];
  totalRegistros: number;
  isLoading?: boolean;
  failed?: boolean;
}

/**
 * Las acciones de este listado llevan su propio reductor adentro, en vez de
 * registrarse en un handleActions: el reducer sólo comprueba que el type sea
 * de esta sección y delega.
 */
interface ListadoEndososAction<P = undefined> {
  type: string;
  payload?: P;
  reducer: (
    state: ListadoEndososState,
    action: ListadoEndososAction<P>
  ) => ListadoEndososState;
}

const endososInitialState: ListadoEndososState = {
  page: 0,
  take: 10,
  searchCriteria: "",
  estado: PENDIENTE,
  endosos: [],
  totalRegistros: 0,
};

export const listadoEndososReducer = (
  state: ListadoEndososState = endososInitialState,
  action: AnyAction
): ListadoEndososState => {
  const isThisReducer = action.type.includes(baseEndososPoliza);
  if (action.reducer && isThisReducer) return action.reducer(state, action);

  return state;
};

export const clearListadoEndosos = (): ListadoEndososAction => {
  return {
    type: `${baseEndososPoliza}/clearListadoEndosos`,
    payload: undefined,
    reducer: () => ({
      ...endososInitialState,
    }),
  };
};

export const selectSearchCriteria = (
  value: string
): ListadoEndososAction<{ value: string }> => {
  return {
    type: `${baseEndososPoliza}/selectSearchCriteria`,
    payload: { value },
    reducer: (state, action) => ({
      ...state,
      searchCriteria: action.payload!.value,
    }),
  };
};

export const selectEstado = (
  value: EstadoValue
): ListadoEndososAction<{ value: EstadoValue }> => {
  return {
    type: `${baseEndososPoliza}/selectEstado`,
    payload: { value },
    reducer: (state, action) => ({
      ...state,
      estado: action.payload!.value,
    }),
  };
};
export const setPage = (
  value: number
): ListadoEndososAction<{ value: number }> => {
  return {
    type: `${baseEndososPoliza}/setPage`,
    payload: { value },
    reducer: (state, action) => ({
      ...state,
      page: action.payload!.value,
    }),
  };
};
export const setTake = (
  value: RowsValue
): ListadoEndososAction<{ value: RowsValue }> => {
  return {
    type: `${baseEndososPoliza}/setTake`,
    payload: { value },
    reducer: (state, action) => ({
      ...state,
      take: action.payload!.value,
    }),
  };
};

const getEndososInited = (): ListadoEndososAction => {
  return {
    type: `${baseEndososPoliza}/getEndososInited`,
    reducer: (state) => ({
      ...state,
      isLoading: true,
    }),
  };
};

const getEndososCompleted = ({
  total,
  items,
}: GetEndososResponse): ListadoEndososAction => {
  return {
    type: `${baseEndososPoliza}/getEndososCompleted`,
    reducer: (state) => ({
      ...state,
      endosos: items,
      totalRegistros: total,
      isLoading: false,
      failed: false,
    }),
  };
};

const getEndososFailed = (error: unknown): ListadoEndososAction => {
  return {
    type: `${baseEndososPoliza}/getEndososFailed`,
    reducer: (state) => ({
      ...state,
      isLoading: false,
      failed: true,
    }),
  };
};

export const getEndosos = ({
  take,
  skip,
  estado,
  searchCriteria,
}: GetEndososRequest) => {
  return async (
    dispatch: (action: any) => void,
    getState: () => any,
    services: Services
  ) => {
    try {
      dispatch(getEndososInited());

      const response = await services.api
        .endosos()
        .getEndosos({ take, skip, estado, searchCriteria });

      dispatch(getEndososCompleted(response));
    } catch (error) {
      console.error(error);
      dispatch(getEndososFailed(error));
      dispatch(
        queueMessage(
          "Ocurrió un error al actualizar la póliza. Intente nuevamente."
        )
      );
    }
  };
};
